import { useContext, useMemo } from "react"
import { useClerk } from "@clerk/react"
import { AuthContext, type AuthContextValue } from "@/providers/AuthProvider"
import { useCurrentUser } from "./useCurrentUser"
import type { AuthUser } from "@/types"

const CLERK_ONLY = () => Promise.reject(new Error("Sign-in is handled by Clerk"))

/** Builds the legacy auth value from the internal current user and Clerk. Used by ClerkAuthBridge. */
export function useAuthValue(): AuthContextValue {
  const { me, isLoading, error } = useCurrentUser()
  const { signOut } = useClerk()
  return useMemo<AuthContextValue>(
    () => ({
      session: me
        ? {
            user: {
              id: me.user_id,
              email: me.email,
              displayName: me.display_name,
              role: me.role as AuthUser["role"],
            },
            accessToken: "", // legacy callers get headers via authHeaders()/getAuthHeaders()
            expiresAt: Number.MAX_SAFE_INTEGER,
          }
        : null,
      isLoading,
      error: error?.message ?? null,
      loginWithGoogle: CLERK_ONLY,
      loginWithPassword: CLERK_ONLY,
      signupWithPassword: CLERK_ONLY,
      logout: () => signOut(),
    }),
    [me, isLoading, error, signOut],
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used inside ClerkAuthBridge")
  return ctx
}
